import React, { useEffect, useState } from 'react';

import { Business } from '@mui/icons-material';
import { Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, ListItem } from '@mui/material';

import { useApiClient, usePersistedSetting, useToastService } from '@@common';

import { TenantViewModel, UserClient } from '../../api/api';
import ListWithFilter from '../../views/FlowDesigner/Components/ListWithFilter';

interface TenantSelectorDialogProps {
	open: boolean;
	onClose: () => void;
	onTenantChanged: (tenant: TenantViewModel) => void;
}

const TenantSelectorDialog: React.FC<TenantSelectorDialogProps> = ({ open, onClose, onTenantChanged }) => {
	const [tenants, setTenants] = useState<TenantViewModel[]>([]);
	const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [activeOnly, setActiveOnly] = usePersistedSetting('tenantSelector.activeOnly', true);
	const userClient = useApiClient(UserClient);
	const toast = useToastService();

	const loadTenants = React.useCallback(async () => {
		setIsLoading(true);
		try {
			const userData = await userClient.getUser(!activeOnly);
			setTenants(
				[...(userData.tenants ?? [])].sort((left, right) => left.code.localeCompare(right.code, undefined, { sensitivity: 'base' })),
			);
		} catch (error) {
			toast.error('Failed to load tenants');
			console.error('Error loading tenants:', error);
		} finally {
			setIsLoading(false);
		}
	}, [activeOnly, userClient, toast]);

	useEffect(() => {
		if (open) {
			loadTenants();
		}
	}, [open, loadTenants]);

	const handleTenantSelect = (tenant: TenantViewModel) => {
		setSelectedTenantId(tenant.id);
	};

	const handleConfirm = async () => {
		if (!selectedTenantId) {
			toast.info('Please select a tenant');
			return;
		}

		const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
		if (!selectedTenant) {
			toast.error('Selected tenant not found');
			return;
		}

		setIsSaving(true);
		try {
			await userClient.setTenant(selectedTenantId);
			toast.success('Tenant changed successfully');
			onTenantChanged(selectedTenant);
			onClose();
		} catch (error) {
			toast.error('Failed to change tenant');
			console.error('Error changing tenant:', error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		if (!isSaving) {
			setSelectedTenantId(undefined);
			onClose();
		}
	};

	useEffect(() => {
		if (selectedTenantId && !tenants.some((tenant) => tenant.id === selectedTenantId)) {
			setSelectedTenantId(undefined);
		}
	}, [selectedTenantId, tenants]);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Select Tenant</DialogTitle>
			<DialogContent>
				<ListWithFilter
					items={tenants}
					isLoading={isLoading}
					loadingIndicator={
						<ListItem sx={{ justifyContent: 'center', py: 3 }}>
							<CircularProgress size={24} aria-label="Loading tenants" />
						</ListItem>
					}
					selectedItemId={selectedTenantId}
					onSelected={handleTenantSelect}
					placeholder="Search tenants..."
					getDisplayName={(tenant) => tenant.code}
					getKey={(tenant) => tenant.id}
					maxHeight="400px"
					emptyMessage="No tenants available"
					icon={() => <Business />}
					itemSx={(tenant) =>
						tenant.isActive === false
							? {
									'& .MuiListItemIcon-root, & .MuiListItemText-primary': {
										color: 'text.disabled',
									},
								}
							: undefined
					}
					trailingContent={(tenant) =>
						tenant.isActive === false ? (
							<Chip label="Disabled" color="warning" size="small" variant="outlined" sx={{ height: 20, ml: 2 }} />
						) : null
					}
					filterActions={
						<FormControlLabel
							control={
								<Checkbox
									checked={activeOnly}
									onChange={(event) => setActiveOnly(event.target.checked)}
									size="small"
								/>
							}
							label="Active Only"
							sx={{
								m: 0,
								whiteSpace: 'nowrap',
								'& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
							}}
						/>
					}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={!selectedTenantId || isSaving}>
					{isSaving ? 'Changing...' : 'Confirm'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TenantSelectorDialog;
